import type { CustomerBusinessStatus, CustomerStatus } from '@plato/app-core/gql/schema-types';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { Badge, type BadgeProps } from '@plato/ui-legacy/components/list-views/badge';
import { SelectBadge, type SelectBadgeOption } from '@plato/ui-legacy/components/list-views/select-badge';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { differenceInCalendarMonths, intlFormatDistance } from 'date-fns';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type OpenTransactionSummary = {
    firstOrderDate?: Date;
    lastOrderDate?: Date;
};

type CustomerFinancials = {
    revenue_ttm_percentage_diff: number;
};

type DescriptionContext = {
    transactionSummary: OpenTransactionSummary | undefined;
    customerFinancials: CustomerFinancials | undefined;
    hasChurnWarnings: boolean | undefined;
};

type CustomerStatusBadgeProps = {
    status: CustomerStatus;
    // When provided AND the user has `canMarkCustomerInactive`, the badge becomes a SelectBadge
    onStatusChangeRequest?: (mode: CustomerBusinessStatus) => void;
} & Partial<DescriptionContext>;

export function CustomerStatusBadge({
    status,
    transactionSummary,
    customerFinancials,
    hasChurnWarnings,
    onStatusChangeRequest,
}: CustomerStatusBadgeProps): ReactNode {
    const { t } = useTranslation();
    const locale = useResolvedLocale();
    const { canMarkCustomerInactive } = useAppConfig();

    const statusConfig: Record<
        CustomerStatus,
        {
            label: string;
            color: BadgeProps['color'];
            getDescription: (ctx: DescriptionContext) => string | undefined;
        }
    > = {
        new: {
            label: t('status_new', { ns: 'common' }),
            color: 'blue',
            getDescription: ({ transactionSummary: summary }) => {
                if (summary === undefined) {
                    return undefined;
                }

                if (summary.firstOrderDate) {
                    return t('status_description_new_with_orders', {
                        daysSinceFirstOrder: intlFormatDistance(summary.firstOrderDate, new Date(), {
                            locale,
                            unit: 'day',
                        }),
                        ns: 'customer-detail',
                    });
                }

                return t('status_description_new_no_orders', { ns: 'customer-detail' });
            },
        },
        active: {
            label: t('status_active', { ns: 'common' }),
            color: 'green',
            getDescription: () => t('status_description_active', { ns: 'customer-detail' }),
        },
        at_risk: {
            label: t('status_at_risk', { ns: 'common' }),
            color: 'orange',
            getDescription: ({ hasChurnWarnings: churn, customerFinancials: financials }) => {
                if (churn === undefined) {
                    return undefined;
                }

                if (churn) {
                    return t('status_description_at_risk_with_churn', {
                        ns: 'customer-detail',
                    });
                }

                if (financials === undefined) {
                    return undefined;
                }

                return t('status_description_at_risk_revenue_decline', {
                    revenueDeclinePercent: financials.revenue_ttm_percentage_diff,
                    formatParams: {
                        revenueDeclinePercent: {
                            style: 'percent',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                            signDisplay: 'never',
                        },
                    },
                    ns: 'customer-detail',
                });
            },
        },
        inactive: {
            label: t('status_inactive', { ns: 'common' }),
            color: 'red',
            getDescription: () => t('status_description_inactive_reason', { ns: 'customer-detail' }),
        },
        dormant: {
            label: t('status_dormant', { ns: 'common' }),
            color: 'red',
            getDescription: ({ transactionSummary: summary }) => {
                if (summary === undefined) {
                    return undefined;
                }

                return t('status_description_dormant', {
                    monthsSinceLastOrder: summary.lastOrderDate
                        ? differenceInCalendarMonths(new Date(), summary.lastOrderDate)
                        : 12,
                    ns: 'customer-detail',
                });
            },
        },
        blocked: {
            label: t('status_blocked', { ns: 'common' }),
            color: 'red',
            getDescription: () => t('status_description_blocked', { ns: 'customer-detail' }),
        },
    };

    const config = statusConfig[status];

    // An ERP block outranks the Plato business status, so there is nothing to switch.
    const isInteractive = !!onStatusChangeRequest && canMarkCustomerInactive && status !== 'blocked';

    if (isInteractive) {
        const description = config.getDescription({
            transactionSummary,
            customerFinancials,
            hasChurnWarnings,
        });

        const currentBinary: CustomerBusinessStatus = status === 'inactive' ? 'inactive' : 'active';

        const options: readonly SelectBadgeOption<CustomerBusinessStatus>[] = [
            { value: 'active', label: t('status_active', { ns: 'common' }), color: 'green' },
            { value: 'inactive', label: t('status_inactive', { ns: 'common' }), color: 'red' },
        ];

        return (
            <SelectBadge<CustomerBusinessStatus>
                value={currentBinary}
                onChange={(next) => {
                    if (next !== currentBinary) {
                        onStatusChangeRequest(next);
                    }
                }}
                options={options}
                triggerLabel={config.label}
                triggerColor={config.color}
                description={description}
                testId="customer-status-badge"
                className="justify-start"
            />
        );
    }

    return (
        <Badge color={config.color} className="justify-start">
            {config.label}
        </Badge>
    );
}
